import { ATTACH_TARGET, BENCH_SPOT, EVOLVE_TARGET, PICK_TARGET, SWITCH_IN } from '../Board/selectors';

const choosing = {
  [ATTACH_TARGET]: { title: 'Attach energy', hint: 'Tap the Pokémon to attach it to.' },
  [BENCH_SPOT]: { title: 'Bench a Pokémon', hint: 'Tap an open bench spot.' },
  [SWITCH_IN]: { title: 'Retreat', hint: 'Tap the benched Pokémon to switch in.' },
  [EVOLVE_TARGET]: { title: 'Evolve', hint: 'Tap the glowing Pokémon to evolve it.' },
  [PICK_TARGET]: { title: 'Choose a Pokémon', hint: 'Tap a glowing Pokémon.' },
};

/**
 * The one line that tells the player where the game stands and what to do next. Game over is shown separately.
 *
 * @param {object} state
 * @param {string|null} state.boardChoice  what the player is picking on the board, from selectBoardChoice
 * @param {boolean} state.hasDeck          the player has chosen a deck
 * @param {boolean} state.started          both players are ready and someone's turn has begun
 * @param {boolean} state.isMyTurn
 * @param {boolean} state.drew             the turn's card has been drawn
 * @param {boolean} state.attackStarted
 * @param {number}  state.prizesToTake     prize cards earned from a knockout and not yet taken
 * @param {boolean} state.hasActive        the player has an active Pokémon
 * @param {boolean} state.hasBench         the player has a Pokémon on the bench
 * @param {boolean} state.opponentHasActive the opponent has an active Pokémon
 * @param {boolean} state.switchInOwed     an attack like Whirlwind hit the player's Pokémon, and they pick who switches in
 * @param {boolean} state.defenderSwitchOwed the player's attack made the opponent switch, and they haven't picked yet
 * @param {number}  state.suddenDeathRound 0 for the game itself, then 1, 2… for each Sudden Death game
 * @param {string|null} state.looking     the Trainer whose player is looking at their deck (Computer Search), if any
 * @param {string|null} state.picking     the Trainer or Power whose Pokémon the player is picking, if any
 */
export function turnStatus(state) {
  if (state.boardChoice === PICK_TARGET && state.picking) {
    return { title: state.picking, hint: choosing[PICK_TARGET].hint };
  }

  if (state.boardChoice && choosing[state.boardChoice]) {
    return choosing[state.boardChoice];
  }

  if (!state.hasDeck) {
    return { title: 'Choose a deck', hint: 'Both players pick a deck to start.' };
  }

  if (!state.started) {
    // Both players won at once, so the game starts over with 1 prize each (rulebook page 25).
    const title = state.suddenDeathRound > 0 ? 'Sudden Death: set up again' : 'Set up';
    if (!state.hasActive) {
      return { title, hint: 'Tap a Basic Pokémon in your hand to make it your active Pokémon.' };
    }
    return { title, hint: 'Bench more Basic Pokémon if you like, then tap Ready.' };
  }

  // A knocked-out active Pokémon must be replaced before anything else, on either player's turn.
  if (!state.hasActive && state.hasBench) {
    return { title: 'Choose a new active Pokémon', hint: 'Tap a Pokémon on your bench.' };
  }

  // Either player can owe a prize: poison can knock out a Pokémon between turns. The turn waits for it.
  if (state.prizesToTake > 0) {
    return {
      title: state.prizesToTake === 1 ? 'Take a prize card' : `Take ${state.prizesToTake} prize cards`,
      hint: 'Tap a glowing prize card.',
    };
  }

  if (state.switchInOwed) {
    // On the opponent's turn, so the banner lights up to show it's waiting on this player.
    return { title: 'Switch your active Pokémon', hint: 'Tap a Pokémon on your bench, then Switch in.', highlight: true };
  }

  if (!state.isMyTurn && state.looking) {
    return { title: "Opponent's turn", hint: `They're looking at their deck for ${state.looking}.` };
  }

  if (!state.isMyTurn) {
    return { title: "Opponent's turn", hint: 'Waiting for them to play.' };
  }

  if (state.looking) {
    return { title: state.looking, hint: 'Choose from your deck.' };
  }

  if (state.defenderSwitchOwed) {
    return { title: 'Your turn', hint: 'Waiting for your opponent to choose a Pokémon to switch in.' };
  }

  if (!state.drew && !state.opponentHasActive) {
    return { title: 'Your turn', hint: 'Waiting for your opponent to choose a new active Pokémon.' };
  }

  if (!state.drew) {
    return { title: 'Your turn', hint: 'Start by drawing a card.' };
  }

  if (state.attackStarted) {
    return { title: 'Attacking', hint: 'Finishing the attack.' };
  }

  return { title: 'Your turn', hint: 'Play cards from your hand, then attack or end your turn.' };
}
